import { getApprovedImagesForProduct } from './approved-product-images';
import {
  getCatalogVariantVolumeMl,
  normalizeCatalogLine,
  parseCatalogProductText,
} from './catalog-name-utils';

export interface GeneratedDemoCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  image?: string;
  sortOrder: number;
}

export interface GeneratedDemoProductVariant {
  id: string;
  size: string;
  volumeMl: number;
  price: number;
  comparePrice?: number;
  stock: number;
  sku: string;
  barcode?: string;
}

export interface GeneratedDemoProduct {
  id: string;
  name: string;
  slug: string;
  description: string;
  categoryId: string;
  brand: string;
  country: string;
  alcoholContent: number;
  images: string[];
  variants: GeneratedDemoProductVariant[];
  tags: string[];
  isActive: boolean;
  isFeatured: boolean;
  rating: number;
  reviewCount: number;
  createdAt: string;
}

type CategoryId =
  | 'cat-spirits'
  | 'cat-beer-cider'
  | 'cat-wine'
  | 'cat-soft-drinks'
  | 'cat-energy'
  | 'cat-water';

interface ParsedInventoryLine {
  name: string;
  variantLabel: string;
  volumeMl: number;
  variantOrder: number;
}

const RAW_INVENTORY_LINES = `
SMIRNOFF 750ML
SMIRNOFF 1/2
SMIRNOFF 1/4
CHROME
NAPPS 750ML
NAPPS 1/4
KIBAO 750ML
KIBAO 1/4
K.C 750ML
K.KING 750ML
K.KING 1/4
CIDER CAN
BEST 1/4
RICHOT 1/2
RICHOT 1/4
ORIGIN 750ML
KONYAGI 1/4
BLUE ICE 1/4
G. MEAKINS 750ML
CARPRICE
FAXE
KANE 1/4
K.C 1/4
ORIGINAL 1/4
WHITE PEARL
HUNTERSCHOICE 1/2
VICEROY BRANDY 1/4
RICHOT 750ML
DEL MONTE
GUARRANA CAN
MORGAN 750ML
HUNTERS 1/4
BEST WHISKEY 750ML
BEST WHISKEY 1/4
VICEROY 750ML
MORGAN 250ML
W/CAP CAN
G.K CAN
TUSKER CAN
VICEROY 1/2
WATER BIG
WATER SMALL
SNAPP CAN
TRIPPLE ACE
BEST GIN
KIBAO 1/2
TEACHERS
KONYAGI 750ML
K.C 350ML
HUNTERS 750ML
JAMESON
REDBULL
MOONWALKER
PILSNER CAN
WHITE PEAL 750ML
BLACK & WHITE 750ML
BLACK & WHITE 500ML
GILBEYS 1/2
GILBEYS 1/4
ALL SEANSONS
KANE 750ML
GILBEYS 750ML
GRANTS 750ML
CRAZY COCK
PENASOL
IMPERIAL BLUE
JOHNNY WALKER
FOURCOUSINS
4TH STREET
ATLAS CAN
PEOPLE VODKA
OPM 750ML
TRIPLE ACE 750ML
SPIRIT
BLACK LABEL 750ML
RED LABEL 750ML
BLACK LABEL 350ML
LIME
BLUE STAR
BOND 1/4
CHROME 750ML
BOUMD 1/2
AMARULA
WILLIAM
VAT 69 750ML
VAT 69 350ML
JOHN WALKER 1LTR
BIG SODA
SMALL SODA
1 LITER SODA
2 LITER SODA
BAILEYS 750ML
TUSKER LITE CAN
BOND 750ML
SMIRNOFF 1 LITER
FAMOUS GROUSE
FAMOUS GROUSE 1 LITER
BLACK ICE
GRANTS 1 LITER
V/A 1/2
V/A 750ML
TONIC SODA
MONSTER ENERGY
PREDATOR
KISS ME NOW
POWER PLAY
TUSKER MALT
4TH STREET 5 LITER
FLIRT VODKA
BLACK & WHITE 1 LITER
IMPERIAL 1/2
IMPERIAL 1/4
COUNTY BRANDY
MAGIC
JACK DANIELS
GRANTS 1/2
CRAZY COCK 1/2
CRAZY COCK 1/4
WILLIAMLAWSON CAN
CLUBMAN 1/2
CLUBMAN 1/4
ROYAL STAG 750ML
IRIS 750ML
BLUE ICE 750ML
MAC MOHAN 750ML
KONYAGI 1/2
ASCONI
OPM 1/2
ALL SEASON 1/2
PASSPORT 750ML
MR DOWOLLS
RESERVE SEVEN
BEST BRAND 750ML
GORDON 750ML
DESPARADO
SAVANA
BEST LONDON 750ML
FLIRT VODKA 1 LITRE
DOUBLE BLACK 750ML
CELLER CASK
BLACK LABEL 1 LITRE
TOP SECRET
BALOZI
WYTHALL
ALTARWINE
BOMBAY
GENERAL 1/4
COUNTY 750ML
BESTCREAM 750ML
SWEET BERRY
LEMONADE
SAFARI
WHITE HOUSE
WHITE CAP GLASS
GUINNESS GLASS
TUSKER GLASS
O.J 16%
O.J 12%
ALL SEASONS 1/4
PLISNER B
UB
KINGSTONE
CARIBIA 750L
R. LABEL 1/4
B. LABEL 1/4
GORDON 1/2
K.O
BALOZI B
`;

const generatedCategoryDefinitions: GeneratedDemoCategory[] = [
  {
    id: 'cat-spirits',
    name: 'Spirits',
    slug: 'spirits',
    description: 'Whiskies, vodkas, brandies, gins, rums, and everyday spirits.',
    sortOrder: 1,
  },
  {
    id: 'cat-beer-cider',
    name: 'Beer & Cider',
    slug: 'beer-cider',
    description: 'Local cans, bottles, glass pours, cider, and malt picks.',
    sortOrder: 2,
  },
  {
    id: 'cat-wine',
    name: 'Wine',
    slug: 'wine',
    description: 'House wines, sweet reds, and easy-pour party bottles.',
    sortOrder: 3,
  },
  {
    id: 'cat-soft-drinks',
    name: 'Soft Drinks',
    slug: 'soft-drinks',
    description: 'Mixers, juice, soda, and refreshers.',
    sortOrder: 4,
  },
  {
    id: 'cat-energy',
    name: 'Energy Drinks',
    slug: 'energy-drinks',
    description: 'Energy boosters and high-caffeine cans.',
    sortOrder: 5,
  },
  {
    id: 'cat-water',
    name: 'Water',
    slug: 'water',
    description: 'Still water in quick-pick sizes.',
    sortOrder: 6,
  },
];

const FEATURED_PRODUCT_NAMES = new Set([
  'SMIRNOFF',
  'BLACK LABEL',
  'RED LABEL',
  'JAMESON',
  'JACK DANIELS',
  'GRANTS',
  'BLACK & WHITE',
  'BAILEYS',
  'BOMBAY',
  'TUSKER',
  'PILSNER',
  'FAMOUS GROUSE',
]);

const PREMIUM_PRODUCT_NAMES = new Set([
  'BLACK LABEL',
  'RED LABEL',
  'DOUBLE BLACK',
  'JACK DANIELS',
  'JAMESON',
  'BAILEYS',
  'BOMBAY',
  'FAMOUS GROUSE',
  'JOHNNY WALKER',
  'GRANTS',
  'VAT 69',
]);

const CATEGORY_PRICE_BASE: Record<CategoryId, number> = {
  'cat-spirits': 700,
  'cat-beer-cider': 260,
  'cat-wine': 950,
  'cat-soft-drinks': 120,
  'cat-energy': 180,
  'cat-water': 60,
};

const VARIANT_MULTIPLIER: Record<string, number> = {
  REGULAR: 1,
  '1/4': 1,
  '250ML': 1,
  '350ML': 1.2,
  '1/2': 1.55,
  '500ML': 1.55,
  '750ML': 2.2,
  '1 LITER': 2.9,
  '2 LITER': 4.3,
  '5 LITER': 9,
  CAN: 1,
  GLASS: 1.35,
  BOTTLE: 1.2,
  SMALL: 1,
  BIG: 1.6,
  '12%': 1.15,
  '16%': 1.25,
};

const VARIANT_ORDER: Record<string, number> = {
  '750ML': 10,
  '1 LITER': 20,
  '1/2': 30,
  '500ML': 40,
  '350ML': 50,
  '1/4': 60,
  '250ML': 70,
  '2 LITER': 80,
  '5 LITER': 90,
  CAN: 100,
  GLASS: 110,
  BOTTLE: 120,
  SMALL: 130,
  BIG: 140,
  '12%': 150,
  '16%': 160,
  REGULAR: 170,
};

export const generatedDemoCategories = generatedCategoryDefinitions;
export const generatedDemoProducts = buildGeneratedDemoProducts();

function buildGeneratedDemoProducts(): GeneratedDemoProduct[] {
  const groupedItems = new Map<string, ParsedInventoryLine[]>();

  RAW_INVENTORY_LINES.split('\n')
    .map((line) => normalizeCatalogLine(line))
    .filter(Boolean)
    .map((line) => parseInventoryLine(line))
    .forEach((item) => {
      const variants = groupedItems.get(item.name) ?? [];
      if (!variants.some((variant) => variant.variantLabel === item.variantLabel)) {
        variants.push(item);
      }
      groupedItems.set(item.name, variants);
    });

  return Array.from(groupedItems.entries())
    .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
    .map(([name, variants], index) => buildProduct(name, variants, index));
}

function buildProduct(
  name: string,
  parsedVariants: ParsedInventoryLine[],
  index: number
): GeneratedDemoProduct {
  const categoryId = resolveCategoryId(name);
  const category = generatedCategoryDefinitions.find((entry) => entry.id === categoryId);
  const featured = FEATURED_PRODUCT_NAMES.has(name);
  const createdAt = featured
    ? new Date(Date.UTC(2026, 3, (index % 25) + 1, 8, 0, 0)).toISOString()
    : new Date(Date.UTC(2023, 10, (index % 25) + 1, 8, 0, 0)).toISOString();
  const sortedVariants = [...parsedVariants].sort(
    (variantA, variantB) =>
      variantA.variantOrder - variantB.variantOrder ||
      variantA.volumeMl - variantB.volumeMl ||
      variantA.variantLabel.localeCompare(variantB.variantLabel)
  );

  const variants = sortedVariants.map((variant, variantIndex) => {
    const price = resolvePrice(categoryId, name, variant.variantLabel);
    const comparePrice =
      featured && variant.variantLabel !== 'REGULAR'
        ? Math.round((price * 1.12) / 10) * 10
        : undefined;

    return {
      id: `var-${slugify(name)}-${slugify(variant.variantLabel)}`,
      size: variant.variantLabel,
      volumeMl: variant.volumeMl,
      price,
      comparePrice,
      stock: resolveStock(variant.variantLabel, variantIndex),
      sku: `${slugify(name).toUpperCase()}-${slugify(variant.variantLabel).toUpperCase()}`,
    };
  });

  const sizeSummary = variants.map((variant) => variant.size).join(', ');
  const productTags = Array.from(
    new Set([
      category?.slug ?? 'inventory',
      ...variants.map((variant) => variant.size.toLowerCase()),
      'local-catalog',
      variants.length > 1 ? 'multi-size' : 'single-size',
    ])
  );
  const approvedImages = getApprovedImagesForProduct({
    productName: name,
    variants: sortedVariants.map((variant) => ({
      label: variant.variantLabel,
      volumeMl: variant.volumeMl,
    })),
  });

  return {
    id: `prod-${slugify(name)}`,
    name,
    slug: slugify(name),
    description:
      variants.length > 1
        ? `${name} is available in ${sizeSummary}. Choose the quantity you want from the popup before adding it to cart.`
        : `${name} is available as a quick-pick item in the catalog.`,
    categoryId,
    brand: name,
    country: categoryId === 'cat-water' || categoryId === 'cat-soft-drinks' || categoryId === 'cat-energy' ? 'Kenya' : 'Imported',
    alcoholContent: resolveAlcoholContent(categoryId),
    images: approvedImages.length > 0 ? approvedImages : ['/placeholder-product.png'],
    variants,
    tags: productTags,
    isActive: true,
    isFeatured: featured,
    rating: featured ? 4.8 : 4.3,
    reviewCount: featured ? 140 + index : 24 + (index % 60),
    createdAt,
  };
}

function parseInventoryLine(line: string): ParsedInventoryLine {
  const parsed = parseCatalogProductText(line);
  return buildParsedLine(parsed.name, parsed.variantLabel, parsed.volumeMl);
}

function buildParsedLine(
  baseName: string,
  variantLabel: string,
  volumeMl: number | null
): ParsedInventoryLine {
  const normalizedName = canonicalizeBaseName(baseName);
  const normalizedVariantLabel = normalizeVariantLabel(variantLabel);

  return {
    name: normalizedName,
    variantLabel: normalizedVariantLabel,
    volumeMl: volumeMl ?? getCatalogVariantVolumeMl(normalizedVariantLabel) ?? 330,
    variantOrder: VARIANT_ORDER[normalizedVariantLabel] ?? 999,
  };
}

function normalizeLine(line: string): string {
  return line
    .replace(/½/g, '1/2')
    .replace(/¼/g, '1/4')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

function canonicalizeBaseName(value: string): string {
  const canonicalValue = normalizeLine(value)
    .replace(/\bTRIPPLE\b/g, 'TRIPLE')
    .replace(/\bPLISNER\b/g, 'PILSNER')
    .replace(/\bPEAL\b/g, 'PEARL')
    .replace(/\bSEANSONS\b/g, 'SEASONS')
    .replace(/\bALL SEASON\b/g, 'ALL SEASONS')
    .replace(/\bW\/CAP\b/g, 'WHITE CAP')
    .replace(/\bR\. LABEL\b/g, 'RED LABEL')
    .replace(/\bB\. LABEL\b/g, 'BLACK LABEL')
    .replace(/\bJOHN WALKER\b/g, 'JOHNNY WALKER')
    .replace(/\bV\/A\b/g, 'VICEROY')
    .replace(/\bVICEROY BRANDY\b/g, 'VICEROY')
    .replace(/\bCOUNTY\b$/g, 'COUNTY BRANDY')
    .replace(/\bBEST\b$/g, 'BEST WHISKEY');

  return canonicalValue;
}

function normalizeVariantLabel(value: string): string {
  const normalizedValue = normalizeLine(value)
    .replace(/^1LTR$/g, '1 LITER')
    .replace(/^1 LTR$/g, '1 LITER')
    .replace(/^1 LITRE$/g, '1 LITER')
    .replace(/^750L$/g, '750ML');

  return normalizedValue === 'B' ? 'BOTTLE' : normalizedValue;
}

function resolveCategoryId(name: string): CategoryId {
  if (/\bWATER\b/.test(name)) {
    return 'cat-water';
  }

  if (
    /\b(REDBULL|MONSTER ENERGY|MONSTER|PREDATOR|POWER PLAY)\b/.test(name)
  ) {
    return 'cat-energy';
  }

  if (
    /\b(SODA|TONIC|LEMONADE|DEL MONTE|LIME|O\.J|GUARRANA|GUARANA)\b/.test(name)
  ) {
    return 'cat-soft-drinks';
  }

  if (
    /\b(4TH STREET|FOURCOUSINS|ASCONI|ALTARWINE|CELLER CASK|SWEET BERRY|WHITE HOUSE|MR DOWOLLS|WYTHALL|IRIS|CARIBIA|PENASOL)\b/.test(name)
  ) {
    return 'cat-wine';
  }

  if (
    /\b(CIDER|HUNTERSCHOICE|HUNTERS|SNAPP|TUSKER|TUSKER LITE|TUSKER MALT|PILSNER|WHITE CAP|GUINNESS|ATLAS|BALOZI|BLUE STAR|SAFARI|FAXE|DESPARADO|SAVANA|UB|K\.O|KINGSTONE|G\.K|WILLIAMLAWSON|BLACK ICE)\b/.test(name)
  ) {
    return 'cat-beer-cider';
  }

  return 'cat-spirits';
}

function resolvePrice(categoryId: CategoryId, productName: string, variantLabel: string): number {
  const basePrice = CATEGORY_PRICE_BASE[categoryId];
  const multiplier = VARIANT_MULTIPLIER[variantLabel] ?? 1;
  const premiumMultiplier = PREMIUM_PRODUCT_NAMES.has(productName) ? 1.45 : 1;

  return Math.max(50, Math.round((basePrice * multiplier * premiumMultiplier) / 10) * 10);
}

function resolveStock(variantLabel: string, variantIndex: number): number {
  const baseStock: Record<string, number> = {
    REGULAR: 24,
    '1/4': 34,
    '250ML': 32,
    '350ML': 24,
    '1/2': 26,
    '500ML': 24,
    '750ML': 22,
    '1 LITER': 14,
    '2 LITER': 10,
    '5 LITER': 6,
    CAN: 42,
    GLASS: 26,
    BOTTLE: 24,
    SMALL: 20,
    BIG: 16,
    '12%': 18,
    '16%': 18,
  };

  return (baseStock[variantLabel] ?? 18) + (variantIndex % 4) * 3;
}

function resolveAlcoholContent(categoryId: CategoryId): number {
  switch (categoryId) {
    case 'cat-spirits':
      return 40;
    case 'cat-beer-cider':
      return 5;
    case 'cat-wine':
      return 12;
    case 'cat-soft-drinks':
    case 'cat-energy':
    case 'cat-water':
    default:
      return 0;
  }
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
